import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Avatar,
  AvatarFallback,
  AvatarImage
} from "./UI";
import { Camera, Check, Loader2 } from "lucide-react";
import { SettingsAPI } from "api/settings/service";
import { useAlert } from "components/UI_components/Alert";

const mapProfile = (profile) => {
  return {
    firstName: profile.first_name,
    lastName: profile.last_name,
    email: profile.user_email,
    phone: profile.contact_number,
    language: profile.language,
    userpic: profile.userpic,
    profession: profile.profession,
    address_id: profile.address_id,
    id: profile.id,
    timezone: "Asia/Kolkata",
  };
};

const professionOptions = [
  "Contractor",
  "Supervisor",
  "Manager",
  "Architect",
  "Engineer",
  "Vendor",
  "Employee",
  "Others",
];

export function ProfileSection() {
  const { addAlert } = useAlert();
  const [profile, setProfile] = useState(mapProfile(JSON.parse(localStorage.getItem("user"))) || {});
  const [address, setAddress] = useState({});
  const [hasAddress, setHasAddress] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const res = await SettingsAPI.getUserProfile();
        const profileData = mapProfile(res.data);
        setProfile(profileData);
        localStorage.setItem("user", JSON.stringify(res.data));

        if (profileData.address_id) {
          try {
            const addressRes = await SettingsAPI.getAddress(profileData.address_id);
            if (addressRes.status && addressRes.data) {
              setAddress({
                street_address: addressRes.data.street_address || "",
                city: addressRes.data.city || "",
                state: addressRes.data.state || "",
                pincode: addressRes.data.pincode || "",
                country: "India",
              });
              setHasAddress(true);
            }
          } catch (error) {
            console.error("Error fetching address:", error);
          }
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };
    fetchProfileData();
  }, []);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // 1. Update Profile Details
      const profilePayload = {
        first_name: profile.firstName,
        last_name: profile.lastName,
        contact_number: profile.phone,
      };

      if (profile.profession !== "owner") {
        profilePayload.profession = profile.profession;
      }

      await SettingsAPI.updateUserDetails(profile.id, profilePayload);

      // 2. Update or Create Address
      const addressPayload = {
        street_address: address.street_address,
        city: address.city,
        state: address.state,
        pincode: address.pincode,
        country: "India",
      };

      if (hasAddress) {
        await SettingsAPI.updateAddress(profile.address_id, addressPayload);
      } else if (Object.values(address).some(v => v && v !== "India")) {
        await SettingsAPI.createAddress(addressPayload);
      }

      addAlert("Your changes have been saved successfully.", "success");
    } catch (error) {
      console.error("Error saving profile:", error);
      addAlert(error.message || "Failed to save changes. Please try again.", "destructive");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageClick = () => {
    document.getElementById("profile-image-input").click();
  };

  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append("userpic", file);

    try {
      const res = await SettingsAPI.updateUserDetails(profile.id, formData);
      if (res.status) {
        setProfile((prev) => ({ ...prev, userpic: res.data.userpic }));
        localStorage.setItem("user", JSON.stringify(res.data));
        addAlert("Profile image updated successfully.", "success");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      addAlert(error.message || "Failed to upload image. Please try again.", "destructive");
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        addAlert("Image size should be less than 2MB.", "destructive");
        return;
      }
      const validTypes = ["image/jpeg", "image/png", "image/gif"];
      if (!validTypes.includes(file.type)) {
        addAlert("Please upload a valid image (JPG, PNG or GIF).", "destructive");
        return;
      }

      // Preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfile((prev) => ({ ...prev, userpic: reader.result }));
      };
      reader.readAsDataURL(file);

      handleImageUpload(file);
    }
  };

  return (
    <div className="space-y-6" style={{
      scrollbarWidth: "none"
    }}>
      <div>
        <h2 className="text-2xl font-semibold text-primary-text">Profile</h2>
        <p className="text-text-gray mt-1">Manage your personal information and preferences.</p>
      </div>

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Profile Photo</CardTitle>
          <CardDescription>This will be displayed across SiteMaster.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <input
              type="file"
              id="profile-image-input"
              accept="image/*"
              className="hidden"
              onChange={handleImageChange}
            />
            <div className="relative">
              <Avatar className="h-20 w-20">
                {profile.userpic ? (
                  <AvatarImage src={profile.userpic} />
                ) : (
                  <AvatarFallback className="bg-sm-primary text-white text-xl">
                    {(profile.firstName?.charAt(0) || "").toUpperCase() + (profile.lastName?.charAt(0) || "").toUpperCase()}
                  </AvatarFallback>
                )}
              </Avatar>
              <button
                onClick={handleImageClick}
                className="absolute bottom-0 right-0 p-1.5 bg-sm-primary text-white rounded-full hover:bg-sm-hover transition-colors shadow-sm"
              >
                <Camera className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="space-y-1">
              <Button onClick={handleImageClick} variant="outline" size="sm">Upload Photo</Button>
              <p className="text-xs text-text-gray">JPG, PNG or GIF. Max 2MB.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Personal Information</CardTitle>
          <CardDescription>Update your personal details here.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={profile.firstName}
                onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={profile.lastName}
                onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={profile.email}
              disabled
              className="bg-gray-bg/50"
            />
            <p className="text-xs text-text-gray">Email cannot be changed. Contact support if needed.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profession">Profession</Label>
              <Select
                value={profile.profession?.toLowerCase()}
                onValueChange={(v) => setProfile({ ...profile, profession: v })}
                disabled={profile.profession === "owner"}
              >
                <SelectTrigger id="profession" className={profile.profession === "owner" ? "bg-gray-bg/50" : ""}>
                  <SelectValue placeholder="Select Profession" />
                </SelectTrigger>
                <SelectContent>
                  {profile.profession === "owner" && (
                    <SelectItem value="owner">Owner</SelectItem>
                  )}
                  {professionOptions.map((opt) => (
                    <SelectItem key={opt} value={opt.toLowerCase()}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {profile.profession === "owner" && (
                <p className="text-xs text-text-gray">Owner profession cannot be changed.</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Address</CardTitle>
          <CardDescription>Your current location and mailing address.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="street">Street Address</Label>
            <Input
              id="street"
              value={address.street_address}
              onChange={(e) => setAddress({ ...address, street_address: e.target.value })}
              placeholder="Street Address"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={address.city}
                onChange={(e) => setAddress({ ...address, city: e.target.value })}
                placeholder="City"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state">State</Label>
              <Input
                id="state"
                value={address.state}
                onChange={(e) => setAddress({ ...address, state: e.target.value })}
                placeholder="State"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="pincode">Pincode</Label>
              <Input
                id="pincode"
                value={address.pincode}
                onChange={(e) => setAddress({ ...address, pincode: e.target.value })}
                placeholder="6-digit pincode"
              />
            </div>
            <div className="space-y-2">
              <Label>Country</Label>
              <Input value="India" disabled className="bg-gray-bg/50" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Preferences</CardTitle>
          <CardDescription>Customize your experience.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Language</Label>
              <Select value={profile.language} onValueChange={(v) => setProfile({ ...profile, language: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="English">English</SelectItem>
                  <SelectItem value="Hindi">हिंदी</SelectItem>
                  <SelectItem value="Tamil">தமிழ்</SelectItem>
                  <SelectItem value="Marathi">मराठी</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Timezone</Label>
              <Select value={profile.timezone} onValueChange={(v) => setProfile({ ...profile, timezone: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Asia/Kolkata">India Standard Time (IST)</SelectItem>
                  <SelectItem value="Asia/Dubai">Gulf Standard Time (GST)</SelectItem>
                  <SelectItem value="Asia/Singapore">Singapore Time (SGT)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} className="gap-2" disabled={isLoading}>
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Check className="h-4 w-4" />
          )}
          {isLoading ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}
